import * as React from 'react';
import {
  endOfMonth,
  endOfWeek,
  isSameDay,
  isSameMonth,
  isSameWeek,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Fab, Typography, useMediaQuery } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { View } from 'react-big-calendar';
import { Appointment, Filter, FilterOption, LoadedUser } from '../interfaces';
import DateNav from '../components/navigation/DateNav';
import MultiSelectFilter from '../components/filtering/MultiSelectFilter';
import FullPageModal from '../components/modal/FullPageModal';
import {
  getAppointmentById,
  invalidateAppointmentPayments,
  invalidateDepositTransferAppointments,
} from '../services/appointments';
import Error from '../components/global/Error';
import { important, isUserAdminOrManager } from '../utils/global/helper';
import WeekView from '../components/calendar/week/weekView';
import useSelectedCalendarArtists from '../hooks/calendar/useSelectedCalendarArtists';
import SingleSelectFilter from '../components/filtering/SingleSelectFilter';
import '../components/calendar/calendar.css';
import client from '../utils/react-query/client';
import useUser from '../hooks/global/useUser';
import useGetAppointments from '../hooks/appointments/useGetAppointments';
import useCheckoutForm from '../hooks/global/useCheckoutForm';
import useCalendarSelectedAppointment from '../hooks/appointments/useCalendarSelectedAppointment';
import useCheckoutSocket from '../hooks/payments/useCheckoutSocket';
import useFeatureGuard from '../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../types/subscriptions';
import useSubscriptionModal from '../hooks/subscriptions/useSubscriptionModal';
import PageLayout from '../components/global/PageLayout';
import { getBusinessTripName } from '../helpers/businesses';
import OnboardingProgress from '../components/onboarding/OnboardingProgress';
import { PAGE_PADDING } from '../constants/ui';
import { FILTER_PERSISTENCE_KEYS, ROUTES } from '../constants/global';
import CalendarNewForm from '../components/form/CalendarNewForm';
import AppointmentSideBarModal from '../components/appointments/AppointmentSideBarModal';
import { getInitialArtistOptions } from '../helpers/employees';
import { getCheckoutErrorMessage } from '../helpers/payments';
import useInSplitCheckout from '../hooks/payments/useInSplitCheckout';
import AlertTooltip from '../components/global/AlertTooltip';
import useAppointmentFormOpen from '../hooks/appointments/useAppointmentFormOpen';
import { isAppointmentDepositTerminalCheckoutLoading } from '../helpers/appointments';
import {
  CalendarEventType,
  CalendarSlotSelection,
  DEPOSIT_STATUS_VALUES,
  DepositFilterStatus,
} from '../types/calendar';
import {
  CALENDAR_EVENT_OPTIONS,
  DEPOSIT_STATUS_OPTIONS,
  UNASSIGNED_RESOURCE_ID,
  WAIVER_STATUS_OPTIONS,
  WaiverFilterStatus,
} from '../constants/calendar';
import {
  getAppointmentDepositFilterStatus,
  getAppointmentWaiverFilterStatus,
  getInitialCalendarEvents,
  getInitialDepositStatus,
  getInitialIncludeInactiveEmployees,
  getInitialTrips,
  getInitialWaiverStatuses,
} from '../helpers/calendar';
import {
  invalidateCustomerGiftCardBalances,
  invalidateGiftCardsPage,
} from '../services/gift-cards';
import CalendarViewToggle from '../components/calendar/CalendarViewToggle';
import ArtistFilterPopover from '../components/calendar/ArtistFilterPopover';
import useBusinessWebRedesign from '../hooks/businesses/useBusinessWebRedesign';
import CalendarFilterPopover, {
  PopoverFilter,
} from '../components/calendar/CalendarFilterPopover';

const DATE_STORAGE_KEY = 'CalendarSelectedDate';
const VIEW_STORAGE_KEY = 'CalendarSelectedView';
const ARTIST_FILTER_NAME = FILTER_PERSISTENCE_KEYS.calendarArtists;
const TYPE_FILTER_NAME = FILTER_PERSISTENCE_KEYS.calendarEvents;
const ARTIST_COLOR_TOOLTIP_STORAGE_KEY = 'CalendarArtistColorTooltip';
const ARTIST_COLOR_TOOLTIP_AUTO_OPEN_UNTIL_DATE = new Date('2025-06-15');
const REDESIGN_VIEW_OPTIONS = [
  { label: 'Day', value: 'day' },
  { label: 'Week', value: 'week' },
  { label: 'Month', value: 'month' },
];

const getInitialView = (options: FilterOption[], defaultView: string) => {
  const cachedValue = sessionStorage.getItem(VIEW_STORAGE_KEY);

  const parsedCachedValue = cachedValue ? JSON.parse(cachedValue) : undefined;

  if (options.some(option => option.value === parsedCachedValue)) {
    return parsedCachedValue;
  }

  return defaultView;
};

const getInitialSelectedDate = () => {
  const cachedValue = sessionStorage.getItem(DATE_STORAGE_KEY);
  const parsedCachedValue = cachedValue ? new Date(cachedValue) : undefined;
  return parsedCachedValue ?? new Date();
};

export default function CalendarPage() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();

  const { user: loadedUser } = useUser<LoadedUser>();
  const { active: redesignActive } = useBusinessWebRedesign();
  const {
    selectedAppointment,
    selectedAppointmentUpdated,
    setSelectedAppointment,
    setSelectedAppointmentUpdated,
    openedAppointment,
    setOpenedAppointment,
  } = useCalendarSelectedAppointment();
  const {
    resetCheckoutFailureData,
    setCheckoutFailureData,
    setTerminalCheckoutModalToFailed,
  } = useCheckoutForm();

  const isDesktop = useMediaQuery('(min-width:900px)');

  const [filteredAppointments, setFilteredAppointments] =
    React.useState<Appointment[]>();

  const artistOptions = React.useMemo(() => {
    if (!loadedUser) return [];
    return getInitialArtistOptions(loadedUser, { includeUnassigned: true });
  }, [loadedUser]);

  const [selectedArtists, setSelectedArtists] = useSelectedCalendarArtists();
  const [includeInactiveEmployees, setIncludeInactiveEmployees] =
    React.useState(getInitialIncludeInactiveEmployees);
  const [selectedEvents, setSelectedEvents] = React.useState<
    CalendarEventType[]
  >(getInitialCalendarEvents());
  const [selectedWaiverStatuses, setSelectedWaiverStatuses] = React.useState<
    WaiverFilterStatus[]
  >(getInitialWaiverStatuses());
  const [selectedDepositStatus, setSelectedDepositStatus] =
    React.useState<DepositFilterStatus>(getInitialDepositStatus());

  const artistFilter = React.useMemo<Filter>(
    () => ({
      name: ARTIST_FILTER_NAME,
      options: artistOptions,
      state: selectedArtists,
      setState: setSelectedArtists,
      emptyLabel: 'Select an artist',
      allLabel: 'All artists',
    }),
    [artistOptions, selectedArtists, setSelectedArtists],
  );

  const eventsFilter = React.useMemo<Filter>(
    () => ({
      name: TYPE_FILTER_NAME,
      options: CALENDAR_EVENT_OPTIONS,
      state: selectedEvents,
      setState: newTypes => {
        setSelectedEvents(newTypes as CalendarEventType[]);
      },
      emptyLabel: 'Select an event',
      allLabel: 'All events',
    }),
    [selectedEvents],
  );

  const waiverFilterName = FILTER_PERSISTENCE_KEYS.calendarWaiverStatus;
  const waiverStatusFilter = React.useMemo<Filter>(
    () => ({
      name: waiverFilterName,
      options: WAIVER_STATUS_OPTIONS,
      state: selectedWaiverStatuses,
      setState: newStatuses => {
        setSelectedWaiverStatuses(newStatuses as WaiverFilterStatus[]);
      },
      emptyLabel: 'Select status',
      allLabel: 'All statuses',
    }),
    [waiverFilterName, selectedWaiverStatuses],
  );

  const tripsOptions = React.useMemo(() => {
    if (
      !loadedUser.business.businessTrips ||
      loadedUser.business.businessTrips.length === 0
    )
      return [];

    const tempTripOptions = [{ label: 'Home Studio', value: '' }];
    loadedUser.business.businessTrips.forEach(trip => {
      tempTripOptions.push({
        label: getBusinessTripName(trip),
        value: trip.tripId,
      });
    });
    return tempTripOptions;
  }, [loadedUser]);

  const [selectedTrip, setSelectedTrip] = React.useState<string[]>(() =>
    getInitialTrips(tripsOptions),
  );

  const tripFilter = React.useMemo(() => {
    if (!tripsOptions || tripsOptions.length === 0) return undefined;
    return {
      name: FILTER_PERSISTENCE_KEYS.calendarTrips,
      options: tripsOptions,
      state: selectedTrip,
      setState: setSelectedTrip,
    };
  }, [selectedTrip, setSelectedTrip, tripsOptions]);

  const secondaryFilters = React.useMemo(() => {
    const filters: PopoverFilter[] = [
      { ...eventsFilter, label: 'Events' },
      { ...waiverStatusFilter, label: 'Waiver Status' },
      {
        label: 'Deposit Status',
        inputType: 'single',
        name: FILTER_PERSISTENCE_KEYS.calendarDepositStatus,
        options: DEPOSIT_STATUS_OPTIONS,
        state: selectedDepositStatus,
        defaultValue: DEPOSIT_STATUS_VALUES.ALL,
        setState: (value: string) =>
          setSelectedDepositStatus(
            (value || DEPOSIT_STATUS_VALUES.ALL) as DepositFilterStatus,
          ),
      },
    ];
    if (tripFilter) {
      filters.splice(1, 0, { ...tripFilter, label: 'Trips' });
    }
    if (isUserAdminOrManager(loadedUser)) {
      filters.push({
        label: 'Inactive employees',
        inputType: 'single',
        name: FILTER_PERSISTENCE_KEYS.calendarInactiveEmployees,
        options: [
          { label: 'Include inactive employees', value: 'true' },
          { label: 'Exclude inactive employees', value: 'false' },
        ],
        state: includeInactiveEmployees ? 'true' : 'false',
        defaultValue: 'true',
        setState: (value: string) =>
          setIncludeInactiveEmployees(value === 'true'),
        isToggle: true,
      });
    }
    return filters;
  }, [
    eventsFilter,
    waiverStatusFilter,
    tripFilter,
    loadedUser,
    includeInactiveEmployees,
    selectedDepositStatus,
  ]);

  const viewOptions = [
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
    { label: 'Day', value: 'day' },
  ];

  const [selectedView, setSelectedView] = React.useState<View>(() =>
    getInitialView(viewOptions, redesignActive || !isDesktop ? 'day' : 'month'),
  );

  const [viewFilter, setViewFilter] = React.useState({
    name: VIEW_STORAGE_KEY,
    options: [
      { label: 'Week', value: 'week' },
      { label: 'Month', value: 'month' },
      { label: 'Day', value: 'day' },
    ],
    state: selectedView,
    setState: setSelectedView,
  });

  const [selectedDate, setSelectedDate] = React.useState(() =>
    getInitialSelectedDate(),
  );
  const [dataStartDate, setDataStartDate] = React.useState(
    startOfWeek(startOfMonth(new Date())),
  );
  const [dataEndDate, setDataEndDate] = React.useState(
    endOfWeek(endOfMonth(new Date())),
  );

  const [newAptModalOpen, setNewAptModalOpen] = useAppointmentFormOpen();
  const [shouldScrollToTop, setShouldScrollToTop] = React.useState(false);
  const [slotSelection, setSlotSelection] =
    React.useState<CalendarSlotSelection | null>(null);

  const EXCLUDED_EMPLOYEE_IDS = new Set([
    'cmdj19l5r018cpcet8htrmyq7',
    'clfsdz7jb0006s60u884bvjoh',
  ]);
  const excludeOtherBusinesses = EXCLUDED_EMPLOYEE_IDS.has(
    loadedUser?.userId ?? '',
  );

  const {
    appointments,
    isError: isErrorAppointments,
    queryKey,
    refetch: refetchAppts,
  } = useGetAppointments({
    dataEndDate,
    dataStartDate,
    refetchInterval: 300000,
    excludeOtherBusinesses,
  });

  const {
    resetTerminalCheckoutModalState,
    setTerminalCheckoutModalToSuccess,
    mostRecentPaymentId,
    terminalCheckoutModal,
  } = useCheckoutForm();

  const { openSubscriptionModal } = useSubscriptionModal();
  const canAddAppointment = useFeatureGuard(Feature.ADD_APPOINTMENT);
  const canManageAppointment = useFeatureGuard(Feature.MANAGE_APPOINTMENT);
  const canManageTeam = useFeatureGuard(Feature.MANAGE_TEAM);

  // Set up click action for each row of the calendar table
  const editAppointment = React.useCallback(() => {
    if (!openedAppointment) return;
    if (!canManageAppointment) {
      openSubscriptionModal();
      return;
    }
    setSelectedAppointment(openedAppointment);
    setNewAptModalOpen(true);
  }, [
    canManageAppointment,
    openSubscriptionModal,
    openedAppointment,
    setNewAptModalOpen,
    setSelectedAppointment,
  ]);

  // Close edit appointment
  const closeEditAppointment = React.useCallback(() => {
    setSelectedAppointment(null);
    setSlotSelection(null);
    setNewAptModalOpen(false);
  }, [setNewAptModalOpen, setSelectedAppointment]);

  // Set up click action for each row of the calendar table
  const openAppointment = React.useCallback(
    (appointment: Appointment) => {
      setOpenedAppointment(appointment);
      resetCheckoutFailureData();
      if (appointment.appointmentId !== appointmentId) {
        navigate(`${ROUTES.calendar}/${appointment.appointmentId}`, {
          replace: true,
          preventScrollReset: true,
        });
      }
    },
    [appointmentId, navigate, resetCheckoutFailureData, setOpenedAppointment],
  );

  const onCloseAppointment = React.useCallback(() => {
    setOpenedAppointment(null);
    if (appointmentId) {
      navigate(ROUTES.calendar, {
        replace: true,
        preventScrollReset: true,
      });
    }
  }, [appointmentId, navigate, setOpenedAppointment]);

  const handleOpen = () => {
    if (!canAddAppointment) {
      openSubscriptionModal();
      return;
    }
    setNewAptModalOpen(true);
  };

  const handleSelectSlot = React.useCallback(
    (slot: CalendarSlotSelection) => {
      if (!canAddAppointment) {
        openSubscriptionModal();
        return;
      }
      setSlotSelection(slot);
      setNewAptModalOpen(true);
    },
    [canAddAppointment, openSubscriptionModal, setNewAptModalOpen],
  );

  const changeStartDate = (newDate: Date) => {
    setSelectedDate(newDate);
  };

  const updateAppointmentInArray = React.useCallback(
    (newAppointment: Appointment) =>
      client.setQueryData(
        queryKey,
        (oldAppointments: Appointment[] | undefined) => {
          const currentAppointments = oldAppointments ?? [];
          const index = currentAppointments.findIndex(
            a => a.appointmentId === newAppointment.appointmentId,
          );
          if (index > -1) {
            return currentAppointments.map((appointment, idx) =>
              idx === index ? newAppointment : appointment,
            );
          }
          return [...currentAppointments, newAppointment];
        },
      ),
    [queryKey],
  );

  const terminalCheckoutModalRef = React.useRef(terminalCheckoutModal);
  React.useEffect(() => {
    terminalCheckoutModalRef.current = terminalCheckoutModal;
  }, [terminalCheckoutModal]);

  const fetchAppointmentAndUpdateModal = React.useCallback(
    async (tempAppointmentId: string, isError?: boolean) => {
      if (!loadedUser) return;
      const response = await getAppointmentById(tempAppointmentId);

      const isDepositTerminalCheckoutLoading =
        isAppointmentDepositTerminalCheckoutLoading(response);
      if (
        response &&
        !response.checkoutSessionActive &&
        !isDepositTerminalCheckoutLoading &&
        terminalCheckoutModalRef.current.status === 'loading' &&
        !isError
      ) {
        resetTerminalCheckoutModalState();
      }

      // Update the appointment in the appointments array
      updateAppointmentInArray(response);
      openAppointment(response);
    },
    [
      loadedUser,
      updateAppointmentInArray,
      openAppointment,
      resetTerminalCheckoutModalState,
    ],
  );

  React.useEffect(() => {
    if (selectedAppointmentUpdated && openedAppointment) {
      fetchAppointmentAndUpdateModal(openedAppointment.appointmentId);
      setSelectedAppointment(null);
      setSelectedAppointmentUpdated(false);
    }
  }, [
    setSelectedAppointment,
    setSelectedAppointmentUpdated,
    fetchAppointmentAndUpdateModal,
    openedAppointment,
    selectedAppointmentUpdated,
  ]);

  React.useEffect(() => {
    if (appointmentId) {
      fetchAppointmentAndUpdateModal(appointmentId);
    }
  }, [appointmentId, fetchAppointmentAndUpdateModal]);

  const isInSplitCheckout = useInSplitCheckout();

  // Backup interval fetching in PaymentSection
  useCheckoutSocket({
    enabled: !isInSplitCheckout,
    onCheckoutCompleted: async data => {
      if (
        (!data.appointmentId ||
          data.appointmentId !== openedAppointment?.appointmentId) &&
        (!data.paymentId || data.paymentId !== mostRecentPaymentId)
      ) {
        return;
      }

      if (data.appointmentId) {
        invalidateDepositTransferAppointments();
        await fetchAppointmentAndUpdateModal(data.appointmentId);
      }

      if (terminalCheckoutModal.status === 'loading') {
        setTerminalCheckoutModalToSuccess({
          paymentId: data.paymentId,
          isGuest: !data.customerId,
        });
      } else {
        resetTerminalCheckoutModalState();
      }

      invalidateCustomerGiftCardBalances();
      invalidateGiftCardsPage();
    },
    onCheckoutFailed: async data => {
      if (
        (!data.appointmentId ||
          data.appointmentId !== openedAppointment?.appointmentId) &&
        (!data.paymentId || data.paymentId !== mostRecentPaymentId)
      ) {
        return;
      }

      setCheckoutFailureData(
        data as Parameters<typeof setCheckoutFailureData>[0],
      );
      const tempFailureMessage = getCheckoutErrorMessage(data);

      setTerminalCheckoutModalToFailed(tempFailureMessage);
      if (data.appointmentId)
        await fetchAppointmentAndUpdateModal(data.appointmentId, true);
    },
    onCheckoutUpdated: async data => {
      if (
        !data.appointmentId ||
        data.appointmentId !== openedAppointment?.appointmentId
      ) {
        return;
      }

      await fetchAppointmentAndUpdateModal(data.appointmentId);
      invalidateAppointmentPayments(data.appointmentId);
    },
  });

  const activeEmployeeIds = React.useMemo(
    () => new Set(loadedUser?.business.employees.map(e => e.employeeId) ?? []),
    [loadedUser],
  );

  React.useEffect(() => {
    if (!appointments) return;
    let tempFilteredAppointments = appointments;

    if (selectedArtists) {
      if (selectedArtists.length !== 1 || selectedArtists[0] !== 'Initialize') {
        tempFilteredAppointments = tempFilteredAppointments.filter(
          (appointment: Appointment) => {
            const id = appointment.employeeId;
            // Inactive employees aren't in selectedArtists; let them through
            // when the "Include inactive employees" toggle is on.
            if (includeInactiveEmployees && id && !activeEmployeeIds.has(id)) {
              return true;
            }
            return selectedArtists.includes(id || UNASSIGNED_RESOURCE_ID);
          },
        );
      }
    }

    if (selectedTrip && selectedTrip.length > 0) {
      tempFilteredAppointments = tempFilteredAppointments.filter(
        appointment => {
          const { businessTripId } = appointment;

          // Check if any ID in selectedTrip matches the businessTripId
          const matchesId =
            businessTripId && selectedTrip.includes(businessTripId);

          // Check if selectedTrip includes an empty string and businessTripId is null or undefined
          const matchesEmptyString =
            selectedTrip.includes('') &&
            (businessTripId === null || businessTripId === undefined);

          // Include the appointment if either condition is true
          return matchesId || matchesEmptyString;
        },
      );
    }

    if (
      selectedWaiverStatuses.length > 0 &&
      selectedWaiverStatuses.length < WAIVER_STATUS_OPTIONS.length
    ) {
      tempFilteredAppointments = tempFilteredAppointments.filter(appointment =>
        selectedWaiverStatuses.includes(
          getAppointmentWaiverFilterStatus(appointment),
        ),
      );
    }

    if (selectedDepositStatus !== DEPOSIT_STATUS_VALUES.ALL) {
      tempFilteredAppointments = tempFilteredAppointments.filter(
        appointment =>
          getAppointmentDepositFilterStatus(appointment) ===
          selectedDepositStatus,
      );
    }

    setFilteredAppointments(tempFilteredAppointments);
  }, [
    selectedArtists,
    appointments,
    selectedTrip,
    selectedWaiverStatuses,
    selectedDepositStatus,
    includeInactiveEmployees,
    activeEmployeeIds,
  ]);

  React.useEffect(() => {
    if (selectedView) {
      setViewFilter({
        ...viewFilter,
        state: selectedView,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView]);

  React.useEffect(() => {
    const startOfMonthData = startOfWeek(startOfMonth(selectedDate));
    const endOfMonthData = endOfWeek(endOfMonth(selectedDate));

    if (startOfMonthData < dataStartDate) {
      setDataStartDate(startOfMonthData);
      setDataEndDate(endOfMonthData);
    }

    if (endOfMonthData > dataEndDate) {
      setDataStartDate(startOfMonthData);
      setDataEndDate(endOfMonthData);
    }
  }, [dataEndDate, dataStartDate, selectedDate]);

  const error = isErrorAppointments ? 'Error loading appointments' : '';

  const returnToToday = () => {
    setSelectedDate(new Date());
    sessionStorage.setItem(DATE_STORAGE_KEY, new Date().toISOString());
  };

  const showTodayButton =
    (selectedView === 'day' && !isSameDay(new Date(), selectedDate)) ||
    (selectedView === 'week' && !isSameWeek(new Date(), selectedDate)) ||
    (selectedView === 'month' && !isSameMonth(new Date(), selectedDate));

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 200px)',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Error errorMessage={error} />
      </Box>
    );
  }

  const showArtistFilterTooltip =
    canManageTeam &&
    loadedUser?.canManageTeam &&
    isUserAdminOrManager(loadedUser) &&
    !!loadedUser?.business?.employees?.length &&
    !loadedUser.business.employees.some(e => e.color);

  return (
    <PageLayout
      title="Calendar"
      topSection={
        <OnboardingProgress
          sx={theme => ({
            mt: 4,
            borderTop: `1px solid ${theme.palette.lightGrey.main}`,
            borderBottom: `1px solid ${theme.palette.lightGrey.main}`,
            mx: PAGE_PADDING,
          })}
        />
      }>
      <Box
        className="header-right"
        sx={{
          display: {
            xs: 'inline',
            md: 'none',
          },
        }}>
        <Button
          variant="contained"
          className="primary hidden-desktop"
          onClick={handleOpen}
          sx={redesignActive ? { borderRadius: '50px' } : undefined}>
          Add New
        </Button>
      </Box>
      {redesignActive ? (
        <Box
          className="calendar-header"
          sx={{ flexWrap: 'wrap', gap: '10px', rowGap: '10px' }}>
          <DateNav
            storageKey={DATE_STORAGE_KEY}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            currentView={selectedView}
            redesignActive
          />
          <CalendarViewToggle
            options={REDESIGN_VIEW_OPTIONS}
            value={selectedView}
            onChange={value => setSelectedView(value as View)}
          />
          {artistFilter && (
            <AlertTooltip
              autoOpen={showArtistFilterTooltip}
              autoOpenUntilDate={ARTIST_COLOR_TOOLTIP_AUTO_OPEN_UNTIL_DATE}
              storageKey={ARTIST_COLOR_TOOLTIP_STORAGE_KEY}
              tooltipContent={
                <Typography
                  variant="caption"
                  sx={{
                    color: 'common.white',
                  }}
                  fontWeight={600}>
                  You can now manage individual employees&apos; colors from the{' '}
                  <Link
                    to={ROUTES.team}
                    style={{
                      color: '#ffffff',
                    }}>
                    Manage Team
                  </Link>{' '}
                  page!
                </Typography>
              }>
              <ArtistFilterPopover
                name={artistFilter.name}
                options={artistFilter.options}
                state={artistFilter.state}
                setState={artistFilter.setState}
              />
            </AlertTooltip>
          )}
          <CalendarFilterPopover filters={secondaryFilters} redesignActive />
          <Box
            className="header-right"
            sx={{ display: { xs: important('none'), md: important('flex') } }}>
            <Button
              variant="contained"
              className="primary"
              onClick={handleOpen}
              sx={{ borderRadius: '50px' }}>
              Add New
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          className="calendar-header"
          sx={{
            flexWrap: 'wrap',
            gap: { xs: 1, md: 1 },
            rowGap: { xs: 1.5, md: 1 },
          }}>
          <Box
            sx={{
              flexShrink: 0,
              order: { xs: 1, md: 0 },
              width: { xs: '100%', md: 'auto' },
            }}>
            <DateNav
              storageKey={DATE_STORAGE_KEY}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              currentView={selectedView}
            />
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              order: 0,
              flex: { xs: 1, md: '0 0 auto' },
              minWidth: 0,
            }}>
            <SingleSelectFilter
              name={viewFilter.name}
              options={viewFilter.options}
              state={viewFilter.state}
              setState={
                viewFilter.setState as React.Dispatch<
                  React.SetStateAction<string>
                >
              }
            />
          </Box>
          {artistFilter && (
            <AlertTooltip
              autoOpen={showArtistFilterTooltip}
              autoOpenUntilDate={ARTIST_COLOR_TOOLTIP_AUTO_OPEN_UNTIL_DATE}
              storageKey={ARTIST_COLOR_TOOLTIP_STORAGE_KEY}
              tooltipContent={
                <Typography
                  variant="caption"
                  sx={{
                    color: 'common.white',
                  }}
                  fontWeight={600}>
                  You can now manage individual employees&apos; colors from the{' '}
                  <Link
                    to={ROUTES.team}
                    style={{
                      color: '#ffffff',
                    }}>
                    Manage Team
                  </Link>{' '}
                  page!
                </Typography>
              }>
              <Box
                sx={{
                  flexShrink: 0,
                  order: 0,
                  flex: { xs: 1, md: '0 0 auto' },
                  minWidth: 0,
                }}>
                <MultiSelectFilter
                  name={artistFilter.name}
                  options={artistFilter.options}
                  state={artistFilter.state}
                  setState={artistFilter.setState}
                  emptyLabel={artistFilter.emptyLabel}
                  allLabel={artistFilter.allLabel}
                />
              </Box>
            </AlertTooltip>
          )}
          <Box sx={{ flexShrink: 0, order: 0 }}>
            <CalendarFilterPopover filters={secondaryFilters} />
          </Box>
          <Box
            className="header-right"
            sx={{
              flexShrink: 0,
              order: 0,
              whiteSpace: 'nowrap',
              display: {
                xs: important('none'),
                md: important('flex'),
              },
            }}>
            <Button
              variant="contained"
              className="primary"
              onClick={handleOpen}>
              Add New
            </Button>
          </Box>
        </Box>
      )}
      <WeekView
        artistOptions={artistOptions.map(option => option.value)}
        appointments={filteredAppointments}
        selectedArtists={selectedArtists || []}
        setSelectedArtists={setSelectedArtists}
        selectedEvents={selectedEvents}
        view={selectedView}
        date={selectedDate}
        setDate={changeStartDate}
        setView={setSelectedView}
        openAppointment={openAppointment}
        refreshModal={fetchAppointmentAndUpdateModal}
        includeInactiveEmployees={includeInactiveEmployees}
        onSelectSlot={handleSelectSlot}
        openedAppointmentId={openedAppointment?.appointmentId}
      />
      {openedAppointment ? (
        <AppointmentSideBarModal
          appointment={openedAppointment}
          isOpen
          onClose={onCloseAppointment}
          onRefresh={async () => {
            await fetchAppointmentAndUpdateModal(
              openedAppointment.appointmentId,
            );
          }}
          onEdit={editAppointment}
          onRefetchAppointments={async () => {
            refetchAppts();
          }}
        />
      ) : null}

      <FullPageModal
        modalOpen={newAptModalOpen}
        setModalOpen={setNewAptModalOpen}
        handleClose={closeEditAppointment}
        shouldScrollTop={shouldScrollToTop}
        setShouldScrollToTop={setShouldScrollToTop}
        name={selectedAppointment ? 'Edit Appointment' : 'Add New'}>
        <CalendarNewForm
          slotSelection={slotSelection}
          onClose={() => {
            setSlotSelection(null);
            setNewAptModalOpen(false);
          }}
          onCreateUpdateSuccess={async params => {
            const { type } = params;
            // get new appointment
            if (type === 'create') await refetchAppts();
            // update appointment
            else {
              const { appointment: appt } = params;
              if (!('appointmentId' in appt)) return;
              await fetchAppointmentAndUpdateModal(appt.appointmentId);
            }
          }}
          setShouldScrollToTop={setShouldScrollToTop}
        />
      </FullPageModal>

      {showTodayButton && !redesignActive && (
        <Fab
          variant="extended"
          sx={{ position: 'fixed', bottom: 64, right: { xs: 32, md: 64 } }}
          color="primary"
          onClick={returnToToday}>
          <RefreshIcon sx={{ mr: 1 }} />
          <Typography variant="h5" color="white">
            Today
          </Typography>
        </Fab>
      )}
    </PageLayout>
  );
}
